import { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'

const AuthContext = createContext({})

const ACTIVE_PROFILE_KEY = 'training_matrix_active_profile_id'

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [profiles, setProfiles] = useState([]) // All profiles for this auth user
  const [userProfile, setUserProfile] = useState(null) // Currently active profile
  const [company, setCompany] = useState(null)
  const [userRole, setUserRole] = useState(null)
  const [loading, setLoading] = useState(true)
  const [permissionsLoaded, setPermissionsLoaded] = useState(false)
  const [permissions, setPermissions] = useState({
    company: []
  })

  // Key for forcing React tree remount on profile/company switch (no page reload needed!)
  const [switchKey, setSwitchKey] = useState(0)
  const [isSwitching, setIsSwitching] = useState(false)

  // Load profile data for a specific profile ID
  const loadProfileData = async (profileId, allProfiles) => {
    try {
      const profile = allProfiles.find(p => p.id === profileId)
      if (!profile) {
        console.error('AuthContext: Profile not found:', profileId)
        return
      }

      console.log('AuthContext: Loading data for profile:', profile.id, profile.email)
      setUserProfile(profile)

      // Fetch permissions for this profile
      await fetchPermissions(profile.id)

      // Get company and user role if exists - fetch in parallel for better performance
      if (profile?.company_id) {
        console.log('AuthContext: Fetching company and role in parallel for:', profile.company_id)

        // Fetch company and role simultaneously
        const [companyResponse, roleResponse] = await Promise.all([
          supabase
            .from('companies')
            .select('*')
            .eq('id', profile.company_id)
            .single(),
          supabase
            .from('company_members')
            .select('role')
            .eq('company_id', profile.company_id)
            .eq('user_id', profile.id)
            .single()
        ])

        console.log('AuthContext: Company response:', companyResponse)
        console.log('AuthContext: Role response:', roleResponse)

        // Set company
        if (companyResponse.error) {
          console.error('AuthContext: Company error:', companyResponse.error)
          setCompany(null)
        } else {
          console.log('AuthContext: Company loaded:', companyResponse.data)
          setCompany(companyResponse.data)
        }

        // Set role
        if (roleResponse.error) {
          console.error('AuthContext: Role error:', roleResponse.error)
          setUserRole(null)
        } else {
          console.log('AuthContext: User role:', roleResponse.data.role)
          setUserRole(roleResponse.data.role)
        }
      } else {
        console.log('AuthContext: No company for profile')
        setCompany(null)
        setUserRole(null)
      }
    } catch (error) {
      console.error('AuthContext: Error loading profile data:', error)
    }
  }

  // Fetch all user profiles and select active one
  const fetchUserData = async (userId) => {
    if (!userId) {
      console.log('AuthContext: No userId provided')
      setProfiles([])
      setUserProfile(null)
      setCompany(null)
      setPermissions({ company: [] })
      setPermissionsLoaded(false)
      return
    }

    console.log('AuthContext: Fetching all profiles for auth ID:', userId)

    try {
      // Fetch ALL profiles for this auth user with company information
      const { data: profilesData, error: profilesError } = await supabase
        .from('user_profiles')
        .select('*, company:companies(id, name)')
        .eq('auth_id', userId)
        .order('created_date', { ascending: true })

      console.log('AuthContext: Profiles response:', profilesData)

      if (profilesError) {
        console.error('AuthContext: Profiles error:', profilesError)
        setProfiles([])
        setUserProfile(null)
        setCompany(null)
        setPermissions({ company: [] })
        return
      }

      if (!profilesData || profilesData.length === 0) {
        console.log('AuthContext: No profiles found for user')
        setProfiles([])
        setUserProfile(null)
        setCompany(null)
        return
      }

      console.log(`AuthContext: Found ${profilesData.length} profile(s)`)
      setProfiles(profilesData)

      // Determine which profile to activate
      let activeProfileId = localStorage.getItem(ACTIVE_PROFILE_KEY)

      // Validate stored profile ID exists
      if (activeProfileId && !profilesData.find(p => p.id === activeProfileId)) {
        console.log('AuthContext: Stored profile ID not found, clearing')
        activeProfileId = null
        localStorage.removeItem(ACTIVE_PROFILE_KEY)
      }

      // If no valid stored profile, use the first one (oldest = primary account)
      if (!activeProfileId) {
        activeProfileId = profilesData[0].id
        localStorage.setItem(ACTIVE_PROFILE_KEY, activeProfileId)
        console.log('AuthContext: Using first profile as active:', activeProfileId)
      }

      // Load data for the active profile
      await loadProfileData(activeProfileId, profilesData)

    } catch (error) {
      console.error('AuthContext: Critical error fetching user data:', error)
      setProfiles([])
      setUserProfile(null)
      setCompany(null)
      setUserRole(null)
      setPermissions({ company: [] })
    }
  }

  // Switch to a different profile (no page reload - uses React key remounting)
  const switchProfile = async (profileId, { navigateTo = '/spaces' } = {}) => {
    if (!profileId) {
      console.error('AuthContext: No profile ID provided for switch')
      return { success: false }
    }

    const profile = profiles.find(p => p.id === profileId)
    if (!profile) {
      console.error('AuthContext: Profile not found for switch:', profileId)
      return { success: false }
    }

    console.log('AuthContext: Switching to profile:', profileId, profile.email)

    // Set switching state for transition animation
    setIsSwitching(true)

    // Save to localStorage
    localStorage.setItem(ACTIVE_PROFILE_KEY, profileId)

    // Load data for new profile
    await loadProfileData(profileId, profiles)

    // Increment switch key to force React tree remount (no page reload!)
    setSwitchKey(prev => prev + 1)

    // Small delay for transition animation
    setTimeout(() => {
      setIsSwitching(false)
    }, 100)

    return { success: true, navigateTo }
  }

  // Fetch user permissions using database functions
  const fetchPermissions = async (userProfileId) => {
    if (!userProfileId) {
      console.log('AuthContext: No userProfileId for permissions')
      setPermissions({ company: [] })
      setPermissionsLoaded(false)
      return
    }

    console.log('AuthContext: Fetching permissions for user:', userProfileId)

    try {
      // Fetch company-level permissions using database function
      const { data: companyPerms, error: companyError } = await supabase
        .rpc('get_user_company_permissions', { p_user_id: userProfileId })

      if (companyError) {
        console.error('AuthContext: Error fetching company permissions:', companyError)
        setPermissions({ company: [] })
        setPermissionsLoaded(true)
        return
      }

      console.log('AuthContext: Company permissions loaded:', companyPerms)

      // Store company permissions
      setPermissions({ company: companyPerms || [] })
      setPermissionsLoaded(true)

    } catch (error) {
      console.error('AuthContext: Critical error fetching permissions:', error)
      setPermissions({ company: [] })
      setPermissionsLoaded(true)
    }
  }

  useEffect(() => {
    let mounted = true

    // Simple initial session check
    const checkInitialSession = async () => {
      console.log('AuthContext: Checking initial session...')

      try {
        const { data: { session } } = await supabase.auth.getSession()

        if (session?.user && mounted) {
          console.log('AuthContext: Initial session found:', session.user.email)
          setUser(session.user)

          // Delay to ensure RLS is ready
          setTimeout(() => {
            if (mounted) {
              fetchUserData(session.user.id)
            }
          }, 200)
        } else {
          console.log('AuthContext: No initial session')
        }
      } catch (error) {
        console.error('AuthContext: Error getting initial session:', error)
      } finally {
        if (mounted) {
          setLoading(false)
        }
      }
    }

    // Simple auth state listener - only for auth changes, not data fetching
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('AuthContext: Auth event:', event)

      if (!mounted) return

      if (event === 'SIGNED_OUT' || !session?.user) {
        console.log('AuthContext: Signed out')
        setUser(null)
        setProfiles([])
        setUserProfile(null)
        setCompany(null)
        setUserRole(null)
        setPermissions({ company: [] })
        setPermissionsLoaded(false)
        setLoading(false)
        localStorage.removeItem(ACTIVE_PROFILE_KEY)
      } else if (event === 'SIGNED_IN' && session?.user) {
        console.log('AuthContext: Signed in:', session.user.email)
        setUser(session.user)

        // Delay to ensure RLS is ready
        setTimeout(() => {
          if (mounted) {
            fetchUserData(session.user.id)
            setLoading(false)
          }
        }, 200)
      } else if (event === 'TOKEN_REFRESHED') {
        console.log('AuthContext: Token refreshed')
        if (session?.user && mounted) {
          setUser(session.user)
        }
      }
    })

    checkInitialSession()

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [])

  const updateProfile = async (updates) => {
    if (!userProfile?.id) return

    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .update(updates)
        .eq('id', userProfile.id)
        .select()
        .single()

      if (error) throw error

      setUserProfile(data)

      // Update in profiles array
      setProfiles(prev => prev.map(p => p.id === data.id ? data : p))

      return data
    } catch (error) {
      console.error('Error updating profile:', error)
      throw error
    }
  }

  // Check company-level permission
  const can = (permission) => {
    return permissions.company.includes('*') || permissions.company.includes(permission)
  }

  // Check if user can access a space (view it)
  const canAccessSpace = async (projectId) => {
    if (!userProfile?.id || !projectId) return false

    try {
      const { data, error } = await supabase
        .rpc('user_can_access_space', {
          p_user_id: userProfile.id,
          p_project_id: projectId
        })

      if (error) {
        console.error('AuthContext: Error checking space access:', error)
        return false
      }

      return data === true
    } catch (error) {
      console.error('AuthContext: Critical error checking space access:', error)
      return false
    }
  }

  // Check if user can manage a space (Admin or Manager with access)
  const canManageSpace = async (projectId) => {
    if (!userProfile?.id || !projectId) return false

    try {
      const { data, error } = await supabase
        .rpc('user_can_manage_space', {
          p_user_id: userProfile.id,
          p_project_id: projectId
        })

      if (error) {
        console.error('AuthContext: Error checking space management:', error)
        return false
      }

      return data === true
    } catch (error) {
      console.error('AuthContext: Critical error checking space management:', error)
      return false
    }
  }

  // Permission helper functions (use can() internally)
  const canInviteUsers = () => can('users.invite')
  const canCreateSpaces = () => can('spaces.create')
  const canManageCompany = () => can('company.edit')
  const canEditTraining = () => can('training.edit') || can('training.assign')
  const canViewAllSpaces = () => can('spaces.viewAll')

  // Demo mode helpers
  const isDemoCompany = () => company?.is_demo === true
  const getDemoExpiresAt = () => company?.demo_expires_at || null
  const isDemoExpired = () => {
    if (!isDemoCompany()) return false
    const expiresAt = getDemoExpiresAt()
    if (!expiresAt) return false
    return new Date(expiresAt) < new Date()
  }

  // Expose user data to ChatFlows widget
  useEffect(() => {
    if (user && userProfile) {
      window.__CHATFLOWS_USER_DATA__ = {
        userId: userProfile.id,
        userEmail: user.email,
        userName: userProfile.full_name || userProfile.name || user.email,
        userRole: userRole,
        companyId: company?.id || null,
        companyName: company?.name || null
      }
    } else {
      window.__CHATFLOWS_USER_DATA__ = {}
    }
  }, [user, userProfile, company, userRole])

  const value = {
    user,
    profiles, // All user profiles
    userProfile, // Currently active profile
    company,
    userRole,
    loading,
    permissionsLoaded,
    permissions,
    // Profile switching (seamless, no page reload)
    switchProfile,
    switchKey, // Key for forcing React tree remount
    isSwitching, // True during profile/company switch transition
    // Permission system
    can, // Check company-level permission
    canAccessSpace, // Check if user can view a space
    canManageSpace, // Check if user can manage a space
    // Permission helper functions
    canInviteUsers,
    canCreateSpaces,
    canManageCompany,
    canEditTraining,
    canViewAllSpaces,
    // Demo mode helpers
    isDemoCompany,
    getDemoExpiresAt,
    isDemoExpired,
    signOut: async () => {
      console.log('AuthContext: Signing out...')
      localStorage.removeItem(ACTIVE_PROFILE_KEY)
      await supabase.auth.signOut()
    },
    refreshUserData: () => user && fetchUserData(user.id),
    updateProfile,
    // Trigger app-wide refresh without page reload (for company switching within same profile)
    triggerAppRefresh: () => {
      setIsSwitching(true)
      setSwitchKey(prev => prev + 1)
      setTimeout(() => setIsSwitching(false), 100)
    }
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
